import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, FilePlus, Loader2, Trash2 } from 'lucide-react';
import { HeaderTemplateOption } from '../../services/invoiceHeaderTemplates';

/**
 * Aufklappmenue der Belegueberschriften neben dem Feld "Überschrift".
 *
 * Gleicher Aufbau und gleiche Interaktion wie CostCenterPicker: waehlen,
 * eigenen Text tippen, "Als Vorlage speichern". Zwei Vorlagenlisten im selben
 * Belegkopf, die sich verschieden bedienen liessen, waeren eine Zumutung.
 *
 * Ein Unterschied, der aus der Sache kommt: die eingebauten Vorschlaege
 * ("Rechnung", "Schlussrechnung", …) tragen keinen Loeschknopf. Sie liegen
 * nicht im Speicher und kaemen beim naechsten Oeffnen wieder — ein Knopf, der
 * das verspraeche, loege.
 *
 * Speichern und Loeschen fuehrt der Editor aus (er haelt die Liste und die
 * Benutzerkennung); diese Komponente meldet nur, was gewuenscht ist.
 */

interface Props {
    options: HeaderTemplateOption[];
    /** Aktueller Text im Feld — das, was "Als Vorlage speichern" sichert. */
    currentText: string;
    disabled?: boolean;
    /** Ein Speichern/Loeschen laeuft gerade. */
    busy?: boolean;
    onPick: (text: string) => void;
    onSaveCurrent: () => void;
    onDelete: (text: string) => void;
}

export const HeaderTextPicker: React.FC<Props> = ({
    options,
    currentText,
    disabled,
    busy,
    onPick,
    onSaveCurrent,
    onDelete,
}) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    // Klick ausserhalb schliesst das Aufklappmenue (Muster CostCenterPicker).
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    // Ohne Rueckfrage ersetzen: im Feld steht eine Zeile, kein selbst
    // geschriebener Absatz — dieselbe Entscheidung wie bei der Kostenstelle.
    const handlePick = (text: string) => {
        onPick(text);
        setOpen(false);
    };

    const alreadySaved = options.some(
        (o) => o.text.trim().toLowerCase() === currentText.trim().toLowerCase(),
    );

    return (
        <div ref={wrapRef} className="relative shrink-0">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                disabled={disabled}
                className="flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                title="Gespeicherte Belegüberschriften"
            >
                Vorlagen
                <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="absolute right-0 z-50 mt-1 w-72 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
                    <div className="max-h-56 overflow-y-auto py-1">
                        {options.map((o) => (
                            <div
                                key={o.text}
                                className="flex items-center gap-2 px-2 hover:bg-blue-50/60"
                            >
                                <button
                                    type="button"
                                    onClick={() => handlePick(o.text)}
                                    disabled={disabled}
                                    className="min-w-0 flex-1 px-2 py-1.5 text-left disabled:opacity-40"
                                    title={o.text}
                                >
                                    <span className="block truncate text-sm text-slate-700">
                                        {o.text}
                                    </span>
                                </button>
                                {o.builtin ? (
                                    // Platzhalter statt Knopf: ohne ihn ruecken
                                    // die eingebauten Eintraege gegenueber den
                                    // eigenen ein und die Liste franst aus.
                                    <span className="shrink-0 px-1.5 py-1.5 text-[11px] uppercase tracking-wide text-slate-300">
                                        Standard
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => onDelete(o.text)}
                                        disabled={busy}
                                        className="shrink-0 rounded p-1.5 text-slate-300 hover:bg-red-50 hover:text-red-500 disabled:opacity-40"
                                        title="Vorlage löschen"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-slate-100 p-1.5">
                        <button
                            type="button"
                            onClick={() => {
                                onSaveCurrent();
                                setOpen(false);
                            }}
                            disabled={busy || disabled || !currentText.trim() || alreadySaved}
                            className="flex w-full items-center gap-2 rounded px-3 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40"
                            title={
                                !currentText.trim()
                                    ? 'Überschrift ist leer'
                                    : alreadySaved
                                      ? 'Steht bereits in den Vorlagen'
                                      : 'Aktuelle Überschrift als Vorlage speichern'
                            }
                        >
                            {busy ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <FilePlus className="h-3.5 w-3.5" />
                            )}
                            Als Vorlage speichern
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
